/**
 * 快捷键注册表 —— 纯逻辑，零 Vue 依赖。
 *
 * 为什么要做成注册表而不是散落的 `if (e.key === ...)`：
 *
 *   1. **能测**。按键匹配的细节（修饰键严格比对、Mac 的 ⌘/Ctrl 语义）
 *      很容易写错，而错了就是「按了没反应」，最难查。
 *   2. **界面能自动列出**。底部的快捷键提示条直接渲染注册表，
 *      不需要在模板里再维护一份说明 —— 两份必然会不同步。
 *   3. **上下文可用性**。每条可以带 `enabled()`，不满足就不显示也不响应。
 *
 * 按键风格参考 Blender：Ctrl 系（而不是 Mac 的 ⌘）。
 * 原因是这些快捷键要在 Mac / Windows / Linux 上一致，
 * 而 macOS 的 ⌘H 被系统占用（隐藏应用），拦不住。
 */

/** 一个键盘组合 */
export interface ShortcutCombo {
  /** 主键，小写；用 KeyboardEvent.key 的小写形式（如 'h' / 'delete' / 'escape'） */
  key: string
  ctrl?: boolean
  meta?: boolean
  shift?: boolean
  alt?: boolean
  /**
   * 按**物理键位**匹配（`KeyboardEvent.code`，如 `'Digit1'`）。
   *
   * ⚠️ 给了它就**只看 code**（+ 修饰键），`key` 只用于展示。为什么需要：
   * macOS 上 `Alt+1` 的 `event.key` 是 `'¡'`（死键），按 `key` 匹配
   * **永远匹配不上**；`code` 与键盘布局、修饰键都无关。
   *
   * 真实动机见 `useShortcuts` 里「切窗口类型」那几条：
   * `Ctrl+数字` 在 Chrome / Windows·Linux 上是**切标签页**（拦不住），
   * 所以改用 `Alt+数字` —— 而 `Alt+数字` 又正好踩上面那个 macOS 的坑。
   */
  code?: string
  /** 展示文本；不给就按修饰键自动拼 */
  display?: string
}

export interface Shortcut {
  id: string
  /** 这一条是干什么的（会显示在底部提示条上） */
  label: string
  /**
   * 键盘组合。留空表示这条只是**鼠标手势**（比如「Ctrl+右键划过=剪线」），
   * 只展示、不参与键盘匹配。
   */
  combos?: ShortcutCombo[]
  /** 鼠标手势的展示文本（没有 combos 时用它） */
  display?: string
  /**
   * **触摸端**的展示文本。
   *
   * 手机上没有键盘，把 `Ctrl+H` 摆在提示条上只会让人困惑 ——
   * 所以触摸端只列手势，并用这里的说法（「单指拖」而不是「左键拖」）。
   */
  touchDisplay?: string
  /**
   * **短标签**（2~4 个字），给窄 UI 用 —— 比如触摸端的浮动工具条。
   *
   * 菜单里能用「删除选中的节点 / 连线」，一个 44px 的按钮上放不下。
   */
  short?: string
  /** 触发。没有 run 的条目是纯提示 */
  run?: () => void
  /** 当前是否可用。不满足则既不显示也不响应 */
  enabled?: () => boolean

  /* ── 这张表还被「别处」渲染，下面两个字段说明它该出现在哪 ──────────
   *
   * ⚠️ 为什么要加上这两个字段，而不是另起一张「命令表」：
   *
   * 左侧工具栏和底部提示条要显示的是**同一批东西**
   * （「打成组」在工具栏是一个按钮、在提示条是 `Ctrl+G`），
   * 它们的 label / enabled / run 必须来自同一处 —— 否则就是
   * `docs/layout-redesign.md` §P5 那个病：同一份状态两个入口，必然漂。
   *
   * 但两者的**呈现范围不同**：提示条只列「有触发方式的」
   * （键盘组合或手势），而工具栏要列「有图标的当前可用命令」。
   * 所以不是再造一张表，而是给这张表补两个「该出现在哪」的开关。
   */

  /**
   * 是否列进**底部快捷键提示条**。默认 `true`。
   *
   * 设为 `false` 的是「有图标、点得到的命令」—— 它们已经在工具栏上
   * 摆着了，再在提示条里占一格只会把真正需要记的键位挤掉。
   * （提示条是一条单行、要横向滚动的窄带，位置很贵。）
   */
  statusBar?: boolean

  /*
   * ⚠️ 这里原来有一个 `toolbar?: { section, icon }` 字段 ——
   * 每条命令声明"上不上工具栏、上哪一段、用什么图标"。
   *
   * **工具栏删掉了**，`toolbarCommands` 也删了，这个字段既没人写也没人读
   * （生产数据里一条都没标过）—— 一并删除。
   * 留着一个空字段比删掉更坏：下一个人会以为它有意义。
   */
}

/** 这条该不该出现在底部提示条里（默认该） */
export function showsInStatusBar(shortcut: Shortcut): boolean {
  return shortcut.statusBar !== false
}

/** 取工具栏某一段的命令（顺序 = 注册表里的顺序） */
/*
 * ⚠️ 这里原来有一个 `toolbarCommands(registry, section)` —— 按 `section`
 * 过滤出工具栏要显示的条目（`context` / `view` 三段）。
 *
 * 工具栏整个删掉之后它没有消费者了，一起删。
 * `section` 这个字段**留着**：它是「这条命令属于哪一类」的语义，
 * 菜单分组还在用它（见 `verify:toolbar` 里对它的断言）。
 */


export function comboMatches(combo: ShortcutCombo, event: KeyboardEvent): boolean {
  /*
   * ⚠️ 给了 `code` 就按**物理键位**比，不比 `key` —— 见 `ShortcutCombo.code`
   * 那段注释（macOS 的 Alt+数字会给出 '¡' 这种字符，按 key 永远匹配不上）。
   */
  if (combo.code) {
    if (event.code !== combo.code) return false
  } else if (event.key.toLowerCase() !== combo.key.toLowerCase()) {
    return false
  }
  if (Boolean(combo.ctrl) !== event.ctrlKey) return false
  if (Boolean(combo.meta) !== event.metaKey) return false
  if (Boolean(combo.shift) !== event.shiftKey) return false
  if (Boolean(combo.alt) !== event.altKey) return false
  return true
}

/** 在哪条快捷键上命中了（没命中返回 undefined） */
/**
 * 找出这次按键该触发哪一条。
 *
 * ⚠️ **没有 `run` 的条目不参与匹配** —— 这条是踩出来的。
 *
 * 提示条里有些条目是**纯展示**的（`{ id: 'pan', display: '中键拖' }`，
 * 它描述的是一个手势，不挂命令）。它们平时没有 `combos`，所以不会挡路；
 * 但**一旦有人给它加了 combos，就会把后面的同名键挡死**：
 *
 * ```text
 *   { id: 'delete', combos: [Delete], enabled: () => hasSelection }   ← 没有 run
 *   { id: 'delete-nodes', combos: [Delete], run: () => … }            ← 永远轮不到
 *   → 选中节点按 Delete：命中第一条 → `!hit.run` → 直接 return → 什么都不发生
 * ```
 *
 * 真机反馈正是这个：「选中节点 Delete 没反应」。它原来靠引擎的
 * `delete-key-code` 兜着（引擎负责删），所以那条空条目一直没被发现；
 * 引擎删掉之后，删除功能就跟着一起没了。
 *
 * 所以判据放在**机制**里：不能执行的条目不参与匹配。
 */
export function findShortcut(
  shortcuts: readonly Shortcut[],
  event: KeyboardEvent,
): Shortcut | undefined {
  for (const shortcut of shortcuts) {
    /* 纯展示条目（没有命令）不参与匹配 —— 见上面的 ⚠️ */
    if (!shortcut.run) continue
    if (shortcut.enabled && !shortcut.enabled()) continue
    for (const combo of shortcut.combos ?? []) {
      if (comboMatches(combo, event)) return shortcut
    }
  }
  return undefined
}

/**
 * 当前可用的快捷键（底部提示条渲染它）。
 *
 * 注意 `enabled` 为假的会被过滤掉 —— 所以提示条是**上下文相关**的，
 * 就像 Blender 底部状态栏那样：没选中东西时不会显示「删除选中的」。
 */
export function activeShortcuts(
  shortcuts: readonly Shortcut[],
  /** 触摸设备：提示条变成**手势指南**，键盘那批不显示 */
  coarse = false,
): Shortcut[] {
  return shortcuts.filter((s) => {
    if (s.enabled && !s.enabled()) return false
    /*
     * 工具栏专属的命令（`statusBar: false`）不进提示条 ——
     * 它们已经有一个看得见、点得到的按钮了。
     * 提示条是一条要横向滚动的窄带，位置要留给「只能靠记的键位」。
     */
    if (!showsInStatusBar(s)) return false
    /*
     * 触摸端没有键盘 —— 只展示手势。
     * 带 touchDisplay 的（比如「框选」）留下来，纯键盘的（Ctrl+H）隐掉。
     */
    if (coarse && (s.combos?.length ?? 0) > 0 && !s.touchDisplay) return false
    return true
  })
}

/* ══════════════════════════════════════════════════════════════
   展示
   ══════════════════════════════════════════════════════════════ */

const MOD_LABEL = 'Ctrl'

/** 把组合拼成「Ctrl+Shift+H」这样的文本 */
export function comboLabel(combo: ShortcutCombo): string {
  if (combo.display) return combo.display
  const parts: string[] = []
  if (combo.ctrl) parts.push(MOD_LABEL)
  if (combo.meta) parts.push('⌘')
  if (combo.alt) parts.push('Alt')
  if (combo.shift) parts.push('Shift')
  parts.push(combo.key.length === 1 ? combo.key.toUpperCase() : prettyKey(combo.key))
  return parts.join(' + ')
}

function prettyKey(key: string): string {
  const map: Record<string, string> = {
    delete: 'Delete',
    backspace: 'Backspace',
    escape: 'Esc',
    arrowup: '↑',
    arrowdown: '↓',
    arrowleft: '←',
    arrowright: '→',
    enter: 'Enter',
  }
  return map[key] ?? key
}

/** 一条快捷键的展示文本；多个组合用 `/` 分隔 */
export function shortcutLabel(shortcut: Shortcut, coarse = false): string {
  if (coarse && shortcut.touchDisplay) return shortcut.touchDisplay
  if (shortcut.display) return shortcut.display
  const combos = shortcut.combos ?? []
  if (combos.length === 0) return ''
  return combos.map(comboLabel).join(' / ')
}

/**
 * 这次按键事件该不该被我们吃掉。
 *
 * 在输入框里打字时不能触发快捷键 —— 否则在「名称」输入框里按 Ctrl+H
 * 会把节点的端口藏起来，很莫名其妙。
 */
export function shouldIgnoreEvent(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null
  if (!target) return false
  const tag = target.tagName?.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  return Boolean(target.isContentEditable)
}
